(function () {
  if (window.AurumVitaRingConfigurator) {
    window.AurumVitaRingConfigurator.scan(document);
    return;
  }

  const instances = new Map();
  const sectionSelector = "[data-avsg-ring]";
  let viewerScriptPromise = null;

  function loadViewerModule(url) {
    if (window.AurumVitaRingViewerModule?.createRingViewer) {
      return Promise.resolve(window.AurumVitaRingViewerModule);
    }
    if (viewerScriptPromise) return viewerScriptPromise;
    viewerScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = url;
      script.async = true;
      script.dataset.avsgRingViewerScript = "true";
      script.addEventListener("load", () => {
        if (window.AurumVitaRingViewerModule?.createRingViewer) {
          resolve(window.AurumVitaRingViewerModule);
          return;
        }
        viewerScriptPromise = null;
        reject(new Error("The 3D viewer loaded without its public API."));
      }, { once: true });
      script.addEventListener("error", () => {
        viewerScriptPromise = null;
        reject(new Error("The 3D viewer asset could not be loaded."));
      }, { once: true });
      document.head.appendChild(script);
    });
    return viewerScriptPromise;
  }

  function encodeConfiguration(configuration) {
    try {
      return window.btoa(JSON.stringify(configuration)).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
    } catch (error) {
      return "";
    }
  }

  function decodeConfiguration(encoded) {
    if (!encoded) return null;
    try {
      const normalised = encoded.replaceAll("-", "+").replaceAll("_", "/");
      const padded = normalised + "=".repeat((4 - (normalised.length % 4)) % 4);
      return JSON.parse(window.atob(padded));
    } catch (error) {
      return null;
    }
  }

  function configurationHash(value) {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(36).toUpperCase().padStart(7, "0").slice(0, 7);
  }

  function createReference(configuration) {
    const now = new Date();
    const date = [now.getFullYear(), String(now.getMonth() + 1).padStart(2, "0"), String(now.getDate()).padStart(2, "0")].join("");
    const entropy = `${JSON.stringify(configuration)}:${now.toISOString()}:${Math.random()}`;
    return `AVSG-${date}-${configurationHash(entropy)}`;
  }

  function supportsWebGL() {
    try {
      const canvas = document.createElement("canvas");
      return Boolean(
        canvas.getContext("webgl2", { failIfMajorPerformanceCaveat: true }) ||
        canvas.getContext("webgl", { failIfMajorPerformanceCaveat: true }),
      );
    } catch (error) {
      return false;
    }
  }

  function isLowPowerDevice() {
    const memory = Number(navigator.deviceMemory || 0);
    const cores = Number(navigator.hardwareConcurrency || 0);
    return (memory > 0 && memory <= 2) || (cores > 0 && cores <= 2);
  }

  class RingConfigurator {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;
      this.abortController = new AbortController();
      this.controls = Array.from(section.querySelectorAll("[data-avsg-ring-option]"));
      this.publicControls = this.controls.filter((control) => !control.closest(".avsg-ring__field--internal"));
      this.summary = section.querySelector("[data-avsg-ring-summary]");
      this.referenceOutput = section.querySelector("[data-avsg-ring-reference]");
      this.status = section.querySelector("[data-avsg-ring-status]");
      this.contact = section.querySelector("[data-avsg-ring-contact]");
      this.resetButton = section.querySelector("[data-avsg-ring-reset]");
      this.sideStonePanel = section.querySelector("[data-avsg-side-stones]");
      this.whiteColourFields = Array.from(section.querySelectorAll("[data-avsg-white-colour]"));
      this.fancyColourFields = Array.from(section.querySelectorAll("[data-avsg-fancy-colour]"));
      this.viewButtons = Array.from(section.querySelectorAll("[data-avsg-ring-view]"));
      this.viewerContainer = section.querySelector("[data-avsg-ring-viewer]");
      this.canvas = section.querySelector("[data-avsg-ring-canvas]");
      this.contactBase = this.contact?.getAttribute("href") || "/pages/contact";
      this.viewerModuleUrl = section.dataset.viewerModule;
      this.viewer = null;
      this.viewerPromise = null;
      this.currentReference = "";
      this.disposed = false;
      this.modelUrls = this.readModelUrls();
      this.restoreSelection();
      this.syncConfigurationUI();
      this.bindEvents();
      this.updateConfiguration({ announce: false, animate: false });
      this.observeViewer();
    }

    readModelUrls() {
      const source = this.section.querySelector("[data-avsg-ring-models]");
      if (!source) return {};
      try {
        return JSON.parse(source.textContent || "{}");
      } catch (error) {
        console.warn("AurumVita ring configurator: model map could not be parsed.", error);
        return {};
      }
    }

    selection() {
      return this.publicControls
        .filter((control) => !control.closest("[hidden]"))
        .reduce((result, control) => {
        const value = control.type === "checkbox" ? control.checked : control.value;
        const label = control.type === "checkbox"
          ? (control.checked ? "Included" : "Not included")
          : (control.options[control.selectedIndex]?.text || control.value);
        result[control.dataset.avsgRingOption] = {
          value,
          label,
          field: control.type === "checkbox"
            ? control.closest("label")?.textContent?.trim() || control.dataset.avsgRingOption
            : control.previousElementSibling?.textContent?.trim() || control.dataset.avsgRingOption,
        };
        return result;
      }, {});
    }

    flatSelection() {
      const selected = this.selection();
      return Object.keys(selected).reduce((result, key) => {
        result[key] = selected[key].value;
        return result;
      }, {});
    }

    restoreSelection() {
      const query = new URLSearchParams(window.location.search);
      let restored = decodeConfiguration(query.get("ring"));
      const queryReference = query.get("ring_ref");
      if (!restored) return;
      for (const control of this.controls) {
        const value = restored[control.dataset.avsgRingOption];
        if (control.type === "checkbox" && typeof value === "boolean") control.checked = value;
        if (value && control.type !== "checkbox" && Array.from(control.options).some((option) => option.value === value)) control.value = value;
      }
      if (restored.colour && !restored.colourMode) {
        const colourMode = this.section.querySelector('[data-avsg-ring-option="colourMode"]');
        const fancyHue = this.section.querySelector('[data-avsg-ring-option="fancyHue"]');
        if (colourMode && fancyHue && ["yellow", "orange", "pink", "red", "purple", "blue", "green", "brown", "grey", "black"].includes(restored.colour)) {
          colourMode.value = "fancy";
          fancyHue.value = restored.colour;
        }
      }
      this.currentReference = queryReference || "";
    }

    bindEvents() {
      const signal = this.abortController.signal;
      for (const control of this.controls) {
        control.addEventListener("change", () => {
          this.currentReference = "";
          this.syncConfigurationUI();
          this.updateConfiguration({ announce: true, animate: true });
        }, { signal });
      }
      this.contact?.addEventListener("click", (event) => this.enquire(event), { signal });
      this.resetButton?.addEventListener("click", () => {
        this.viewer?.reset();
        this.canvas?.focus({ preventScroll: true });
      }, { signal });
      for (const button of this.viewButtons) {
        button.addEventListener("click", () => {
          this.viewer?.setView(button.dataset.avsgRingView);
          this.viewButtons.forEach((candidate) => candidate.setAttribute("aria-pressed", candidate === button ? "true" : "false"));
        }, { signal });
      }
      this.canvas?.addEventListener("keydown", (event) => this.handleViewerKeydown(event), { signal });
    }

    configurationData() {
      return window.AurumVitaRingViewerModule?.CONFIGURATION || null;
    }

    syncConfigurationUI() {
      const data = this.configurationData();
      const cutControl = this.section.querySelector('[data-avsg-ring-option="cut"]');
      const settingControl = this.section.querySelector('[data-avsg-ring-option="setting"]');
      const sourceControl = this.section.querySelector('[data-avsg-ring-option="source"]');
      const colourModeControl = this.section.querySelector('[data-avsg-ring-option="colourMode"]');
      const colourGradeControl = this.section.querySelector('[data-avsg-ring-option="colourGrade"]');
      const fancyHueControl = this.section.querySelector('[data-avsg-ring-option="fancyHue"]');
      const fancyIntensityControl = this.section.querySelector('[data-avsg-ring-option="fancyIntensity"]');
      const internalColourControl = this.section.querySelector('[data-avsg-ring-option="colour"]');
      const sidePlacementControl = this.section.querySelector('[data-avsg-ring-option="sidePlacement"]');
      if (!cutControl || !settingControl || !data) return;

      const isFancy = colourModeControl?.value === "fancy";
      this.whiteColourFields.forEach((field) => { field.hidden = isFancy; });
      this.fancyColourFields.forEach((field) => { field.hidden = !isFancy; });
      if (isFancy && fancyHueControl && fancyIntensityControl) {
        const definition = data.fancyDiamondColours?.[fancyHueControl.value];
        const sourceKey = sourceControl?.value === "natural" ? "natural" : "lab";
        const available = definition?.[sourceKey] || ["fancy"];
        for (const option of fancyIntensityControl.options) option.disabled = !available.includes(option.value);
        if (fancyIntensityControl.options[fancyIntensityControl.selectedIndex]?.disabled) fancyIntensityControl.value = available[0];
        if (internalColourControl) internalColourControl.value = definition?.visual || "pink";
      } else if (internalColourControl && colourGradeControl) {
        internalColourControl.value = data.whiteDiamondGrades?.[colourGradeControl.value]?.visual || "colourless";
      }

      const cut = cutControl.value;
      const settings = data.settings || {};
      for (const option of settingControl.options) {
        const definition = settings[option.value];
        const compatible = Boolean(definition) && (definition.compatibleCuts === "all" || definition.compatibleCuts.includes(cut));
        option.disabled = !compatible;
      }
      if (settingControl.options[settingControl.selectedIndex]?.disabled) {
        const firstCompatible = Array.from(settingControl.options).find((option) => !option.disabled);
        if (firstCompatible) settingControl.value = firstCompatible.value;
      }

      const setting = settings[settingControl.value];
      const supportsSideStones = Boolean(setting?.sideStones);
      if (sidePlacementControl) {
        const supportedPlacements = setting?.sidePlacements || [];
        for (const option of sidePlacementControl.options) option.disabled = !supportedPlacements.includes(option.value);
        if (sidePlacementControl.options[sidePlacementControl.selectedIndex]?.disabled) {
          sidePlacementControl.value = supportedPlacements[0] || "shoulders";
        }
      }
      if (this.sideStonePanel) {
        const panelWasVisible = !this.sideStonePanel.hidden;
        this.sideStonePanel.hidden = !supportsSideStones;
        this.sideStonePanel.setAttribute("aria-hidden", supportsSideStones ? "false" : "true");
        const enabledControl = this.sideStonePanel.querySelector('[data-avsg-ring-option="sideEnabled"]');
        if (enabledControl) {
          if (!supportsSideStones) {
            if (panelWasVisible) this.sideStoneEnabledMemory = enabledControl.checked;
            enabledControl.checked = false;
          } else if (!panelWasVisible && this.sideStoneEnabledMemory !== undefined) {
            enabledControl.checked = this.sideStoneEnabledMemory;
            this.sideStoneEnabledMemory = undefined;
          } else if (!panelWasVisible && !enabledControl.checked) {
            enabledControl.checked = true;
          }
        }

        this.sideStonePanel.querySelectorAll(":scope > .avsg-ring__field").forEach((field) => {
          field.hidden = !supportsSideStones || !enabledControl?.checked;
        });
      }
    }

    observeViewer() {
      if (!supportsWebGL()) {
        this.showFallback(this.section.dataset.fallbackMessage);
        return;
      }
      if (!("IntersectionObserver" in window)) {
        this.loadViewer();
        return;
      }
      this.lazyObserver = new IntersectionObserver((entries) => {
        const visible = Boolean(entries[0]?.isIntersecting);
        if (visible) this.loadViewer();
        this.viewer?.setActive(visible);
      }, { rootMargin: "360px" });
      this.lazyObserver.observe(this.viewerContainer);
    }

    async loadViewer() {
      if (this.viewerPromise || this.disposed) return this.viewerPromise;
      this.section.dataset.avsgRingState = "loading";
      this.viewerPromise = loadViewerModule(this.viewerModuleUrl)
        .then((module) => module.createRingViewer({
          container: this.viewerContainer,
          canvas: this.canvas,
          initialConfig: this.flatSelection(),
          modelUrls: this.modelUrls,
          hdrUrl: this.section.dataset.hdrUrl || "",
          dracoPath: this.section.dataset.dracoPath || "",
          background: this.section.dataset.viewerBackground || "#171411",
          environmentIntensity: Number(this.section.dataset.environmentIntensity || 110) / 100,
          initialAngle: Number(this.section.dataset.initialAngle || 25),
          autoRotate: this.section.dataset.autoRotate === "true",
          resumeAutoRotate: this.section.dataset.resumeAutoRotate === "true",
          lowPower: isLowPowerDevice(),
          reducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
          onReady: (details) => {
            if (this.disposed) return;
            this.section.dataset.avsgRingState = "ready";
            this.section.dataset.avsgRingThree = details.threeRevision;
            this.setStatus(this.section.dataset.readyMessage);
          },
          onLoading: (loading) => {
            this.section.dataset.avsgRingModelLoading = loading ? "true" : "false";
          },
          onInteraction: () => {
            this.section.dataset.avsgRingInteracted = "true";
          },
          onContextLost: () => this.showFallback(this.section.dataset.contextMessage),
          onContextRestored: () => {
            this.section.dataset.avsgRingState = "ready";
            this.setStatus(this.section.dataset.readyMessage);
          },
        }))
        .then((viewer) => {
          if (this.disposed) {
            viewer.dispose();
            return null;
          }
          this.viewer = viewer;
          this.syncConfigurationUI();
          this.updateConfiguration({ announce: false, animate: false });
          return viewer;
        })
        .catch((error) => {
          console.error("AurumVita ring configurator: 3D viewer failed to initialise.", error);
          this.showFallback(this.section.dataset.fallbackMessage);
          return null;
        });
      return this.viewerPromise;
    }

    showFallback(message) {
      this.section.dataset.avsgRingState = "fallback";
      this.setStatus(message || this.section.dataset.fallbackMessage);
    }

    updateConfiguration({ announce = true, animate = true } = {}) {
      this.syncConfigurationUI();
      const selected = this.selection();
      const text = Object.values(selected).map((entry) => `${entry.field}: ${entry.label}`).join(" | ");
      this.summary.textContent = text;
      this.canvas?.setAttribute("aria-label", `${this.section.dataset.previewLabel}: ${text}`);
      this.updateReference();
      this.updateContactHref(selected, text);
      if (this.viewer) {
        this.viewer.updateConfig(this.flatSelection(), { animate })
          .then(() => {
            const diagnostics = this.viewer?.getDiagnostics?.();
            if (!diagnostics) return;
            this.section.dataset.avsgRingGeometry = diagnostics.valid ? "valid" : "warning";
            this.section.dataset.avsgRingCut = diagnostics.cut;
            this.section.dataset.avsgRingSetting = diagnostics.setting;
            this.section.dataset.avsgRingProfile = diagnostics.profile;
            this.section.dataset.avsgRingMetal = diagnostics.metal;
            this.section.dataset.avsgRingDiamondBody = diagnostics.diamondBody;
            this.section.dataset.avsgRingDiamondAttenuation = diagnostics.diamondAttenuation;
            this.section.dataset.avsgRingDiamondTransmission = diagnostics.diamondTransmission;
            this.section.dataset.avsgRingDiamondDispersion = diagnostics.diamondDispersion;
            this.section.dataset.avsgRingSideDiamondBody = diagnostics.sideDiamondBody;
            this.section.dataset.avsgRingSideDiamondAttenuation = diagnostics.sideDiamondAttenuation;
            this.section.dataset.avsgRingSideDiamondTransmission = diagnostics.sideDiamondTransmission;
            this.section.dataset.avsgRingSideDiamondDispersion = diagnostics.sideDiamondDispersion;
          })
          .catch((error) => {
            console.error("AurumVita ring configurator: configuration update failed.", error);
            this.showFallback(this.section.dataset.fallbackMessage);
          });
      }
      if (announce) this.setStatus(this.section.dataset.readyMessage);
    }

    updateReference() {
      if (!this.referenceOutput) return;
      this.referenceOutput.textContent = this.currentReference
        ? `${this.section.dataset.referenceLabel}: ${this.currentReference}`
        : "";
    }

    updateContactHref(selected, text) {
      if (!this.contact) return;
      const destination = new URL(this.contactBase, window.location.origin);
      destination.searchParams.set("interest", this.section.dataset.interest);
      destination.searchParams.set("design", text);
      this.contact.setAttribute("href", destination.pathname + destination.search);
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message || "";
    }

    setBusy(busy) {
      this.section.dataset.avsgRingSaving = busy ? "true" : "false";
      this.contact?.setAttribute("aria-disabled", busy ? "true" : "false");
    }

    async composeEnquiryPreview(sourceImage, record, selected) {
      const width = 1400;
      const height = 1680;
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d", { alpha: false });
      context.fillStyle = "#171411";
      context.fillRect(0, 0, width, height);

      const glow = context.createRadialGradient(700, 570, 30, 700, 570, 620);
      glow.addColorStop(0, "rgba(191,151,82,0.14)");
      glow.addColorStop(1, "rgba(23,20,17,0)");
      context.fillStyle = glow;
      context.fillRect(0, 0, width, 1160);

      context.textAlign = "center";
      context.fillStyle = "#d7b778";
      context.font = "600 28px Arial, sans-serif";
      context.fillText("AURUMVITASG", width / 2, 76);
      context.fillStyle = "#f8f6f2";
      context.font = "500 52px Georgia, serif";
      context.fillText("Private Ring Design", width / 2, 140);

      if (sourceImage) {
        try {
          const image = new Image();
          image.decoding = "async";
          image.src = sourceImage;
          await image.decode();
          context.drawImage(image, 170, 190, 1060, 1060);
        } catch (error) {
          console.warn("AurumVita ring configurator: captured ring image could not be composited.", error);
        }
      } else {
        context.fillStyle = "rgba(248,246,242,0.08)";
        context.fillRect(170, 190, 1060, 1060);
        context.fillStyle = "rgba(248,246,242,0.72)";
        context.font = "400 32px Arial, sans-serif";
        context.fillText("3D preview unavailable — specification retained", width / 2, 730);
      }

      context.fillStyle = "rgba(248,246,242,0.14)";
      context.fillRect(140, 1288, 1120, 1);
      context.textAlign = "left";
      context.fillStyle = "#d7b778";
      context.font = "700 23px Arial, sans-serif";
      context.fillText(`DESIGN REFERENCE  ${record.reference}`, 160, 1342);
      context.fillStyle = "#f8f6f2";
      context.font = "400 27px Arial, sans-serif";
      const entries = Object.values(selected);
      entries.forEach((entry, index) => {
        const column = index % 2;
        const row = Math.floor(index / 2);
        const x = column === 0 ? 160 : 745;
        const y = 1404 + row * 62;
        context.fillStyle = "rgba(248,246,242,0.58)";
        context.font = "700 18px Arial, sans-serif";
        context.fillText(entry.field.toUpperCase(), x, y);
        context.fillStyle = "#f8f6f2";
        context.font = "400 25px Arial, sans-serif";
        context.fillText(entry.label, x, y + 29);
      });
      return canvas.toDataURL("image/jpeg", 0.9);
    }

    async prepareEnquiry() {
      if (this.section.dataset.avsgRingSaving === "true") return null;
      this.setBusy(true);
      this.setStatus(this.section.dataset.savingMessage);
      const selected = this.selection();
      let sourceImage = "";
      await this.loadViewer();
      const configuration = this.viewer?.getConfiguration?.() || this.flatSelection();
      const reference = this.currentReference || createReference(configuration);
      if (this.viewer) {
        try {
          sourceImage = await this.viewer.capture({ size: 1200 });
        } catch (error) {
          console.warn("AurumVita ring configurator: preview capture was unavailable; preparing the specification only.", error);
        }
      }
      const record = {
        version: 2,
        reference,
        configuration,
        labels: Object.keys(selected).reduce((result, key) => {
          result[key] = selected[key].label;
          return result;
        }, {}),
        pageUrl: `${window.location.origin}${window.location.pathname}`,
        createdAt: new Date().toISOString(),
      };
      const previewImage = await this.composeEnquiryPreview(sourceImage, record, selected);
      try {
        window.sessionStorage.setItem("aurumvitaRingEnquiry:latest", JSON.stringify({ ...record, previewImage }));
      } catch (error) {
        console.warn("AurumVita ring configurator: private enquiry preview storage is unavailable.", error);
      }
      this.currentReference = reference;
      this.updateReference();
      this.updateContactHref(selected, this.summary.textContent);
      this.setStatus(`${this.section.dataset.savedMessage} ${reference}`);
      this.setBusy(false);
      return { ...record, previewImage };
    }

    async enquire(event) {
      event.preventDefault();
      if (this.section.dataset.avsgRingSaving === "true") return;
      const record = await this.prepareEnquiry();
      if (!record) return;
      const selected = this.selection();
      const enquiry = [
        `${this.section.dataset.referenceLabel}: ${record.reference}`,
        ...Object.values(selected).map((entry) => `${entry.field}: ${entry.label}`),
        record.configuration.dimensions
          ? `Stone dimensions: ${record.configuration.dimensions.length} × ${record.configuration.dimensions.width} × ${record.configuration.dimensions.depth} ${record.configuration.dimensions.unit}`
          : "",
        record.configuration.lengthWidthRatio ? `Length-to-width ratio: ${record.configuration.lengthWidthRatio}` : "",
        `Page: ${record.pageUrl}`,
        `Created: ${record.createdAt}`,
        this.section.dataset.previewStoredMessage,
      ].filter(Boolean).join("\n");
      const destination = new URL(this.contactBase, window.location.origin);
      destination.searchParams.set("interest", this.section.dataset.interest);
      destination.searchParams.set("design", enquiry);
      destination.searchParams.set("ring_ref", record.reference);
      window.location.assign(destination.toString());
    }

    handleViewerKeydown(event) {
      if (!this.viewer) return;
      const actions = {
        ArrowLeft: { azimuth: -0.12 },
        ArrowRight: { azimuth: 0.12 },
        ArrowUp: { polar: -0.1 },
        ArrowDown: { polar: 0.1 },
        "+": { zoom: 0.9 },
        "=": { zoom: 0.9 },
        "-": { zoom: 1.1 },
      };
      if (event.key === "Home") {
        event.preventDefault();
        this.viewer.reset();
        return;
      }
      const action = actions[event.key];
      if (!action) return;
      event.preventDefault();
      this.viewer.nudge(action);
    }

    resize() {
      this.viewer?.resize();
    }

    activate() {
      this.loadViewer();
      this.viewer?.setActive(true);
    }

    dispose() {
      if (this.disposed) return;
      this.disposed = true;
      this.abortController.abort();
      this.lazyObserver?.disconnect();
      this.viewer?.dispose();
      this.viewer = null;
      instances.delete(this.section);
    }
  }

  function scan(root) {
    const candidates = [];
    if (root.matches?.(sectionSelector)) candidates.push(root);
    candidates.push(...root.querySelectorAll?.(sectionSelector) || []);
    for (const section of candidates) {
      if (instances.has(section)) continue;
      instances.set(section, new RingConfigurator(section));
    }
  }

  function destroy(root) {
    for (const [section, instance] of instances) {
      if (section === root || root.contains?.(section)) instance.dispose();
    }
  }

  window.AurumVitaRingConfigurator = { scan, destroy };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => scan(document), { once: true });
  } else {
    scan(document);
  }

  document.addEventListener("shopify:section:load", (event) => scan(event.target));
  document.addEventListener("shopify:section:unload", (event) => destroy(event.target));
  document.addEventListener("shopify:section:reorder", (event) => {
    for (const [section, instance] of instances) {
      if (section === event.target || event.target.contains?.(section)) instance.resize();
    }
  });
  document.addEventListener("shopify:block:select", (event) => {
    const section = event.target.closest?.(sectionSelector);
    instances.get(section)?.activate();
  });
})();
